import { useEffect, useState } from 'react';
import {
  Box,
  Chip,
  CircularProgress,
  InputAdornment,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import api from '../../api/axios';

interface RuleSummary {
  rule_id: string;
  name: string;
  status: string;
  jurisdiction: string;
  sector: string;
  archetype: string;
  subject_type: string;
  origin: string;
  current_version: number;
  updated_at: string;
}

interface Props {
  selectedId?: string;
  onSelect: (id: string) => void;
  /** Bump this to force a re-fetch — e.g. after a rule's status changes in the detail pane. */
  refreshKey?: number;
}

const STATUS_OPTIONS = ['all', 'proposed', 'active', 'archived'];

// Jurisdiction codes that mean "nationwide" — pinned above the states.
const FEDERAL_CODES = new Set(['US', 'USA', 'FEDERAL', 'FED']);

/** Order jurisdictions for the dropdown: federal/nationwide first, then
 *  states alphabetically. */
function orderJurisdictions(codes: string[]): string[] {
  const federal = codes.filter((c) => FEDERAL_CODES.has(c.toUpperCase())).sort();
  const rest = codes.filter((c) => !FEDERAL_CODES.has(c.toUpperCase())).sort();
  return [...federal, ...rest];
}

const RuleList = ({ selectedId, onSelect, refreshKey = 0 }: Props) => {
  const [rules, setRules] = useState<RuleSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [jurisdictionFilter, setJurisdictionFilter] = useState<string>('all');
  const [search, setSearch] = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    const params: Record<string, string> = {};
    if (statusFilter !== 'all') params.status = statusFilter;
    api
      .get('/explore/rules/', { params })
      .then((res) => {
        if (!cancelled) setRules(res.data.rules || []);
      })
      .catch((err) => {
        if (!cancelled) setError(err?.response?.data?.error || err.message || 'failed to load');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [statusFilter, refreshKey]);

  // Distinct jurisdictions present in the loaded rules, federal-first.
  const jurisdictionOptions = orderJurisdictions(
    Array.from(new Set(rules.map((r) => r.jurisdiction).filter(Boolean))),
  );

  const filtered = rules.filter((r) => {
    if (jurisdictionFilter !== 'all' && r.jurisdiction !== jurisdictionFilter) {
      return false;
    }
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      r.rule_id.toLowerCase().includes(q) ||
      r.name.toLowerCase().includes(q) ||
      r.archetype.toLowerCase().includes(q) ||
      (r.jurisdiction || '').toLowerCase().includes(q)
    );
  });

  return (
    <Box>
      {/* Header + filters */}
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', backgroundColor: 'background.paper' }}>
        <Typography variant="overline" color="text.secondary">
          Rules
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
          {loading ? 'loading…' : `${filtered.length} rule${filtered.length === 1 ? '' : 's'}`}
        </Typography>
        <Box sx={{ mt: 1 }}>
          <TextField
            size="small"
            fullWidth
            placeholder="Search rules, archetype, jurisdiction…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        </Box>
        <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
          <Select
            size="small"
            value={jurisdictionFilter}
            onChange={(e) => setJurisdictionFilter(e.target.value as string)}
            sx={{ flex: 1 }}
            renderValue={(v) => (v === 'all' ? 'All jurisdictions' : (v as string))}
          >
            <MenuItem value="all">All jurisdictions</MenuItem>
            {jurisdictionOptions.map((j) => (
              <MenuItem key={j} value={j}>{j}</MenuItem>
            ))}
          </Select>
          <Select
            size="small"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as string)}
            sx={{ flex: 1 }}
            renderValue={(v) => (v === 'all' ? 'All statuses' : (v as string))}
          >
            {STATUS_OPTIONS.map((s) => (
              <MenuItem key={s} value={s}>{s === 'all' ? 'All statuses' : s}</MenuItem>
            ))}
          </Select>
        </Box>
      </Box>

      {/* List */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress size={24} />
        </Box>
      )}
      {error && (
        <Box sx={{ p: 2 }}>
          <Typography color="error" variant="body2">{error}</Typography>
        </Box>
      )}
      {!loading && !error && filtered.length === 0 && (
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            No rules match
          </Typography>
        </Box>
      )}
      {filtered.map((r) => {
        const active = r.rule_id === selectedId;
        return (
          <Box
            key={r.rule_id}
            onClick={() => onSelect(r.rule_id)}
            sx={{
              px: 2,
              py: 1.5,
              borderBottom: 1,
              borderColor: 'divider',
              cursor: 'pointer',
              backgroundColor: active ? 'rgba(58, 68, 82, 0.08)' : 'transparent',
              borderLeft: active ? '3px solid' : '3px solid transparent',
              borderLeftColor: active ? 'primary.main' : 'transparent',
              '&:hover': { backgroundColor: active ? 'rgba(58, 68, 82, 0.12)' : 'rgba(0,0,0,0.03)' },
              transition: 'background-color 0.12s',
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
                {r.name || r.rule_id}
              </Typography>
              <Chip label={r.status} size="small" color={statusColor(r.status)} sx={{ height: 18, fontSize: '0.65rem' }} />
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
              {r.rule_id} · v{r.current_version}
            </Typography>
            {r.archetype && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                {r.jurisdiction && <>{r.jurisdiction} · </>}
                {r.sector && <>{r.sector} · </>}
                {r.archetype}
              </Typography>
            )}
          </Box>
        );
      })}
    </Box>
  );
};

function statusColor(status: string): 'default' | 'success' | 'warning' | 'error' {
  switch (status) {
    case 'active': return 'success';
    case 'proposed': return 'warning';
    case 'archived': return 'default';
    default: return 'default';
  }
}

export default RuleList;
